import { useEffect, useState } from "react";
import { useAuth } from "../auth/store";
import { Link, Outlet, createRoute, type AnyRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { RuleSource } from "@backstory/protocol";
import { RequireStaff } from "../auth/RequireStaff";
import { setApiOrg } from "../api";
import { SessionPage } from "../routes/SessionPage";
import { DomainsPage } from "../routes/org/DomainsPage";
import { SupportAccessPage } from "../routes/org/SupportAccessPage";
import { StaffActivityPage } from "../features/staff/StaffActivityPage";
import { StaffOrgPage } from "../features/staff/StaffOrgPage";
import { StaffEnterprisePage } from "../features/staff/StaffEnterprisePage";
import { StaffOrgsPage } from "../features/staff/StaffOrgsPage";
import { StaffAvailabilityPage } from "../features/staff/StaffAvailabilityPage";
import { StaffPaymentsPage } from "../features/staff/StaffPaymentsPage";
import { StaffAiFeedbackPage } from "../features/staff/StaffAiFeedbackPage";
import { StaffSessionsPage } from "../features/staff/StaffSessionsPage";
import { StaffSystemIssuePage } from "../features/staff/StaffSystemIssuePage";
import { StaffCapacityPage } from "../features/staff/StaffCapacityPage";
import { StaffSystemIssuesPage } from "../features/staff/StaffSystemIssuesPage";
import { StaffTeamPage } from "../features/staff/StaffTeamPage";
import { HelpListPage, HelpTicketPage } from "../features/help/HelpPages";
import { ReportDialog } from "../features/help/ReportDialog";
import { StaffHelpPage, StaffHelpTicketPage, StaffKnowledgePage } from "../features/help/StaffHelpPages";
import { AiAdminLayout, AiModelsPage, AiProvidersPage, AiRoutingPage } from "../features/ai-admin/AiAdminPage";
import { MailAdminPage } from "../features/mail-admin/MailAdminPage";
import { SignInAdminPage } from "../features/signin-admin/SignInAdminPage";
import { GitHubAdminPage } from "../features/github-admin/GitHubAdminPage";
import { StorageAdminPage } from "../features/storage-admin/StorageAdminPage";
import { billingKeys } from "../features/billing/api";
import { saasBillingApi } from "../features/billing/api.saas";
import { staffKeys, supportAccessApi } from "../features/staff/api";

export const deploymentMode = "saas" as const;

export const domainClaimedLoginMessage = "This domain was claimed. Free accounts are no longer valid.";

export function DeploymentSubscriptionBanner() {
  const auth = useAuth();
  const orgId = auth.currentOrgId;
  const state = useQuery({
    queryKey: billingKeys.state(orgId ?? "none"),
    queryFn: () => saasBillingApi.state(orgId!),
    enabled: !!orgId,
    retry: 0,
    refetchInterval: 60_000,
  });
  const [, tick] = useState(0);
  useEffect(() => {
    if (!state.data?.accessEndsAt || state.data.accessDisabled) return;
    const id = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, [state.data?.accessEndsAt, state.data?.accessDisabled]);
  if (!orgId || !state.data) return null;
  if (state.data.accessDisabled && state.data.accessDisabledReason === "payment") {
    return (
      <div className="banner danger subscription-banner" role="alert">
        This account is paused until the invoice is paid. Recording is stopped.{" "}
        <Link to="/orgs/$org/billing" params={{ org: orgId }}>Update payment</Link>
      </div>
    );
  }
  if (state.data.accessDisabled) {
    return (
      <div className="banner danger subscription-banner" role="alert">
        Subscription access has ended. <Link to="/orgs/$org/billing" params={{ org: orgId }}>Reactivate or manage billing</Link>
      </div>
    );
  }
  if (state.data.readOnly) {
    return (
      <div className="banner danger subscription-banner" role="alert">
        This account is read-only until the payment method is updated.{" "}
        <Link to="/orgs/$org/billing" params={{ org: orgId }}>Update payment</Link>
      </div>
    );
  }
  if (!state.data.accessEndsAt) return null;
  const ms = Math.max(0, new Date(state.data.accessEndsAt).getTime() - Date.now());
  if (ms === 0) {
    return (
      <div className="banner danger subscription-banner" role="alert">
        Subscription access has ended. <Link to="/orgs/$org/billing" params={{ org: orgId }}>Reactivate or manage billing</Link>
      </div>
    );
  }
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  return (
    <div className="banner warn subscription-banner" role="status">
      Subscription access ends in <strong>{days > 0 ? `${days}d ${hours}h` : `${hours}h ${minutes}m`}</strong>.{" "}
      <Link to="/orgs/$org/billing" params={{ org: orgId }}>Manage subscription</Link>
    </div>
  );
}

/**
 * The platform console is an area of the product, not a second product: it
 * renders inside the ordinary application shell so operators keep one set of
 * navigation. Leaving the area drops the impersonated organization header that
 * the customer drill-in sets.
 */
function StaffArea() {
  useEffect(() => () => setApiOrg(undefined), []);
  return (
    <RequireStaff>
      <Outlet />
    </RequireStaff>
  );
}

export function deploymentRoutes(_rootRoute: AnyRoute, appRoute: AnyRoute): { root: AnyRoute[]; app: AnyRoute[] } {
  const staffRoute = createRoute({ getParentRoute: () => appRoute, id: "staff", component: StaffArea });
  const staffHomeRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff", component: StaffOrgsPage });
  const staffPaymentsRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/payments", component: StaffPaymentsPage });
  const staffAvailabilityRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/availability", component: StaffAvailabilityPage });
  const staffMailRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/mail", component: MailAdminPage });
  const staffSignInRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/signin", component: SignInAdminPage });
  const staffGitHubRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/github", component: GitHubAdminPage });
  const staffStorageRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/storage", component: StorageAdminPage });
  const staffAiRoute = createRoute({
    getParentRoute: () => staffRoute,
    id: "staff-ai",
    component: () => (
      <AiAdminLayout providersTo="/staff/ai" modelsTo="/staff/ai/models" routingTo="/staff/ai/routing">
        <Outlet />
      </AiAdminLayout>
    ),
  });
  const staffAiProvidersRoute = createRoute({ getParentRoute: () => staffAiRoute, path: "/staff/ai", component: AiProvidersPage });
  const staffAiModelsRoute = createRoute({ getParentRoute: () => staffAiRoute, path: "/staff/ai/models", component: AiModelsPage });
  const staffAiRoutingRoute = createRoute({ getParentRoute: () => staffAiRoute, path: "/staff/ai/routing", component: AiRoutingPage });
  // Not /staff/ai/feedback: reviewing ratings is not part of provider setup, and a
  // path under it would keep the AI setup tabs and sidebar row lit while reading it.
  const staffAiFeedbackRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/ai-feedback", component: StaffAiFeedbackPage });
  const staffCapacityRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/capacity", component: StaffCapacityPage });
  const staffSystemRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/system", component: StaffSystemIssuesPage });
  const staffSystemIssueRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/system/$id", component: StaffSystemIssuePage });
  const staffTeamRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/team", component: StaffTeamPage });
  const staffActivityRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/activity", component: StaffActivityPage });
  const staffHelpRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/help", component: StaffHelpPage });
  const staffHelpTicketRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/help/$id", component: function StaffHelpTicketRoute() {
    const { id } = staffHelpTicketRoute.useParams();
    return <StaffHelpTicketPage id={id} />;
  } });
  const staffKnowledgeRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/help/knowledge", component: StaffKnowledgePage });
  const helpRoute = createRoute({ getParentRoute: () => appRoute, path: "/help", component: HelpListPage });
  const helpTicketRoute = createRoute({ getParentRoute: () => appRoute, path: "/help/$id", component: function HelpTicketRoute() {
    const { id } = helpTicketRoute.useParams();
    return <HelpTicketPage id={id} />;
  } });
  const staffOrgRoute = createRoute({
    getParentRoute: () => staffRoute,
    path: "/staff/orgs/$org",
    component: StaffOrgPage,
    validateSearch: (s: Record<string, unknown>) => (typeof s.tab === "string" && s.tab ? { tab: s.tab } : {}) as { tab?: string },
  });
  const staffSessionsRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/orgs/$org/sessions", component: StaffSessionsPage });
  const staffEnterpriseRoute = createRoute({ getParentRoute: () => staffRoute, path: "/staff/orgs/$org/enterprise", component: StaffEnterprisePage });
  const staffSessionRoute = createRoute({
    getParentRoute: () => staffRoute,
    path: "/staff/orgs/$org/sessions/$id",
    component: SessionPage,
    validateSearch: (s: Record<string, unknown>) => s as { t?: number | string },
  });

  return {
    root: [],
    app: [
      staffRoute.addChildren([
        staffHomeRoute,
        staffPaymentsRoute,
        staffAvailabilityRoute,
        staffMailRoute,
        staffSignInRoute,
        staffGitHubRoute,
        staffStorageRoute,
        staffAiRoute.addChildren([staffAiProvidersRoute, staffAiModelsRoute, staffAiRoutingRoute]),
        staffAiFeedbackRoute,
        staffCapacityRoute,
        staffSystemRoute,
        staffSystemIssueRoute,
        staffTeamRoute,
        staffActivityRoute,
        staffHelpRoute,
        staffKnowledgeRoute,
        staffHelpTicketRoute,
        staffOrgRoute,
        staffEnterpriseRoute,
        staffSessionsRoute,
        staffSessionRoute,
      ]),
      createRoute({ getParentRoute: () => appRoute, path: "/orgs/$org/domains", component: DomainsPage }),
      createRoute({ getParentRoute: () => appRoute, path: "/orgs/$org/support-access", component: SupportAccessPage }),
      helpRoute,
      helpTicketRoute,
    ],
  };
}

export const deploymentOrgNavItems = [
  { to: "domains", label: "Domains" },
  { to: "support-access", label: "Support access" },
] as const;
export const deploymentAuditActions = ["support.access"] as const;
export const deploymentAuditLabels: Readonly<Record<string, string>> = {
  "support.access": "Support request",
  "support.access.requested": "Support access requested",
  "support.access.granted": "Support access granted",
  "support.access.denied": "Support access denied",
  "support.access.revoked": "Support access revoked",
  "support.access.used": "Support access used",
};

/** Support access requests waiting on an admin are counted beside the menu entry. */
export function useDeploymentOrgNavBadges(orgId: string, enabled: boolean): Readonly<Record<string, number>> {
  const grants = useQuery({
    queryKey: staffKeys.grants(orgId),
    queryFn: () => supportAccessApi.list(orgId),
    enabled,
    refetchInterval: 30_000,
  });
  const waiting = (grants.data?.items ?? []).filter((g) => g.status === "pending").length;
  return { "support-access": waiting };
}
export const deploymentRuleSources: ReadonlyArray<{ id: RuleSource; label: string; group: string; hint: string }> = [
  { id: "support.access", label: "Support access", group: "Security & privacy", hint: "Backstory requested, received, or lost time-boxed access to this workspace." },
];

type StaffNavItem = { to: string; label: string; group?: string; exact?: boolean };

/** Platform pages. A group label starts a section; items after it stay in that section. */
const STAFF_NAV: StaffNavItem[] = [
  { to: "/staff", label: "Customers", group: "Customers", exact: true },
  { to: "/staff/ai", label: "AI setup", group: "Platform" },
  { to: "/staff/mail", label: "Email" },
  { to: "/staff/signin", label: "Sign-in" },
  { to: "/staff/github", label: "GitHub" },
  { to: "/staff/storage", label: "Storage" },
  { to: "/staff/payments", label: "Payments" },
  { to: "/staff/availability", label: "Availability" },
  { to: "/staff/ai-feedback", label: "AI feedback" },
  { to: "/staff/activity", label: "Activity", group: "Operations" },
  { to: "/staff/capacity", label: "Capacity" },
  { to: "/staff/system", label: "System issues" },
  { to: "/staff/help", label: "Help desk" },
  { to: "/staff/team", label: "Platform team" },
];

/**
 * One entry in the product rail. Opening it replaces the rail with the
 * platform list, the same way an organization page does.
 */
export function DeploymentSidebarGroup({ staff, onSelect }: { staff: boolean; onSelect: () => void }) {
  if (!staff) return null;
  return (
    <div className="side-group">
      <div className="side-group-label">Platform</div>
      <Link to="/staff" onClick={onSelect} title="Platform">
        <svg className="nav-ic" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 4h7v7H4zm9 0h7v7h-7zM4 13h7v7H4zm9 0h7v7h-7z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
        <span className="side-label">Platform</span>
      </Link>
    </div>
  );
}

/** The platform list, drawn in the app sidebar while a platform page is open. */
export function DeploymentStaffNav({ onNavigate }: { onNavigate: () => void }) {
  const groups: { label: string; links: StaffNavItem[] }[] = [];
  for (const item of STAFF_NAV) {
    const label = item.group ?? groups.at(-1)?.label ?? "Platform";
    const group = item.group || groups.length === 0 ? { label, links: [] } : groups[groups.length - 1]!;
    if (group.links.length === 0) groups.push(group);
    group.links.push(item);
  }
  return (
    <nav className="side-nav" aria-label="Platform">
      <div className="side-group">
        <Link to="/" activeOptions={{ exact: true }} onClick={onNavigate}>
          <span className="side-label">Back to sessions</span>
        </Link>
      </div>
      {groups.map((g) => (
        <div key={g.label} className="side-group">
          <div className="side-group-label">{g.label}</div>
          {g.links.map((l) => (
            <Link key={l.to} to={l.to} onClick={onNavigate} activeOptions={l.exact ? { exact: true } : undefined} title={l.label}>
              <span className="side-label">{l.label}</span>
            </Link>
          ))}
        </div>
      ))}
    </nav>
  );
}

/** The customer's own requests: status, replies, and a place to write back. */
export function DeploymentHelpLink({ onSelect }: { onSelect: () => void }) {
  return (
    <Link to="/help" onClick={onSelect} title="Help">
      <svg className="nav-ic" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.7" />
        <path d="M9.5 9.5a2.5 2.5 0 1 1 3.2 2.4c-.7.3-1.2.8-1.2 1.6V14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M12 17h.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
      <span className="side-label">Help</span>
    </Link>
  );
}

/** Opens the report dialog for the signed-in workspace. Absent when nobody is signed in. */
export function DeploymentHelpButton() {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  if (!auth.currentOrgId) return null;
  return (
    <>
      <button type="button" className="btn ghost icon-btn" onClick={() => setOpen(true)} aria-label="Report" title="Report">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 4v16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M6 5h11l-2.2 3.5L17 12H6" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <ReportDialog orgId={auth.currentOrgId} onClose={() => setOpen(false)} />}
    </>
  );
}

export function DeploymentUserMenuItem(_props: { staff: boolean; owner: boolean; onSelect: () => void }) {
  return null;
}
